import { Injectable } from '@angular/core';
import {
	CachePolicy,
	CachePolicyEntity,
	camelToSnake,
	UrlService,
} from '@datachain/ui-sdk/common';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { Observable } from 'rxjs';

import { CharacteristicType } from '../../core/dc-core-types';
import { elementCodeNormalizer } from '../../core/utils/element-code-normalizer';
import { DepotHeaderItemEntity } from '../../depot/components/create-entity-from-depot-modal/depot-header-item.entity';
import { SubscriptionForDepotItemEntity } from '../../depot/components/depot-creation-modal/available-subscription-item.entity';
import { DepotSchemaConfig } from '../../depot/domain/depot-schema-config';
import { AppMetadataEntity } from '../../ui/components/app-metadata/app-metadata.entity';
import { DcBaseRequester } from '../core/dc-base.requester';
import {
	ColumnTypeMap,
	ICharacteristicType,
} from '../core/models/characteristic-type.model';
import { IMetadataTag } from '../core/models/metadata.model';
import {
	IRepositoryModel,
	ISubscriptionRepositoryModel,
} from './depot-create.model';
import {
	IDataLinkRepoVersion,
	IDepotEntityBulkCount,
	IDepotEntityCreate,
	IDepotHeaderResponse,
	IDepotResponse,
	IDepotSchemaModel,
} from './depot.model';

@Injectable({
	providedIn: 'root',
})
export class DepotRequester extends DcBaseRequester<IDepotResponse> {
	protected resource;
	protected elementsListUrl;
	protected singleElementUrl;

	public constructor(private readonly urlService: UrlService) {
		super();
		this.resource = this.generateResourceUrl('DEPOTS');
		this.elementsListUrl = this.resource;
		this.singleElementUrl = this.resource;
	}

	public getDepotList(
		useCache: boolean
	): Observable<ReadonlyArray<IDepotResponse>> {
		const resourceUrl = this.generateResourceUrl('DEPOTS_DETAILED');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
				details: true,
			}
		);
		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					maxAgeInSeconds: 0,
					isCacheable: false,
			  });
		return this.get<Array<IDepotResponse>>(url, cachePolicy);
	}

	public getEntitiesCountByDepot(depotId: number): Observable<number> {
		const resourceUrl = this.generateResourceUrl('ENTITIES_BY_DEPOT_COUNT');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				depotId,
			},
			{
				project: this.projectId,
			}
		);
		return this.get<number>(
			url,
			CachePolicyEntity.build({
				policy: CachePolicy.CacheOnly,
				maxAgeInSeconds: 30,
				isCacheable: true,
			})
		);
	}

	public entitiesByDepotBulkCount(
		ids: Array<number>,
		useCache: boolean
	): Observable<ReadonlyArray<IDepotEntityBulkCount>> {
		const resourceUrl = this.generateResourceUrl(
			'ENTITIES_BY_DEPOT_BULK_COUNT'
		);
		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
				depotsId: ids,
			}
		);
		return this.get<ReadonlyArray<IDepotEntityBulkCount>>(
			url,
			CachePolicyEntity.build({
				policy: CachePolicy.CacheOnly,
				maxAgeInSeconds: 2,
				isCacheable: true,
			})
		);
	}

	public deleteDepot(depotId: number, type: string): Observable<void> {
		const resourceUrl = this.generateResourceUrl(this.getApiKeyByType(type));
		let url = '';
		if (type.includes('_V2') || type === 'TRINO' || type === 'DATALINK') {
			url = this.urlService.generateUrl(
				resourceUrl,
				{
					repositoryId: depotId,
				},
				{
					project: this.projectId,
					ctx: 0,
				}
			);
		} else {
			url = this.urlService.generateUrl(
				resourceUrl,
				{
					depotId,
				},
				{
					project: this.projectId,
					ctx: 0,
				}
			);
		}
		return this.http.delete<void>(url);
	}

	public deleteMany(depotsIds: Array<number>): Observable<void> {
		const resourceUrl = this.generateResourceUrl('DEPOTS');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
				depotsIds,
				ctx: 0,
			}
		);
		return this.http.delete<void>(url);
	}

	public changeActivationStatusDepot(
		depotId: number,
		type: string,
		isActive: boolean
	): Observable<void> {
		const resourceUrl = this.generateResourceUrl(
			this.getActiveStatusApiKeyByType(type)
		);
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				depotId,
				type,
			},
			{
				project: this.projectId,
				active: isActive,
			}
		);
		return this.http.put<void>(url, {});
	}

	public getDepotHeaders(
		depotId: number,
		type: string,
		useCache: boolean
	): Observable<ReadonlyArray<IDepotHeaderResponse>> {
		const resourceUrl = this.generateResourceUrl(
			this.getHeadersApiKeyByType(type)
		);
		let url;
		if (type === 'TRINO' || type === 'DATALINK') {
			const repositoryId = depotId;
			url = this.urlService.generateUrl(
				resourceUrl,
				{
					repositoryId,
				},
				{
					project: this.projectId,
				}
			);
		} else {
			url = this.urlService.generateUrl(
				resourceUrl,
				{
					depotId,
				},
				{
					project: this.projectId,
				}
			);
		}
		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					maxAgeInSeconds: 0,
					isCacheable: false,
			  });
		return this.get<Array<IDepotHeaderResponse>>(url, cachePolicy);
	}

	// FIXME: entities should not bleed into requester layer
	public createEntityFromDepot(
		depotId: number,
		metadata: AppMetadataEntity,
		sourceDepotHeaders: Array<DepotHeaderItemEntity>,
		mappings: Array<DepotHeaderItemEntity>,
		availableCharacteristicTypes: Array<CharacteristicType>
	): Observable<number> {
		const body: IDepotEntityCreate = this.generateDepotEntityPayload(
			metadata,
			sourceDepotHeaders,
			mappings,
			availableCharacteristicTypes
		);
		const resourceUrl = this.generateResourceUrl('ENTITY_FROM_DEPOT');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				depotId,
			},
			{
				createRepDetails: false,
				project: this.projectId,
			}
		);

		return this.http.post<number>(url, body);
	}

	public getSchema(params: DepotSchemaConfig): Observable<IDepotSchemaModel> {
		let url = this.generateResourceUrl('REPOSITORIES_SCHEMA');
		url = this.urlService.generateUrl(
			url,
			{},
			{
				project: this.projectId,
				ctx: params.selectedContext,
				['detect_types']: params.detectTypes,
			}
		);

		let body: Record<any, any> = {
			['connector_id']: params.connectorId,
			['infer_schema']: params.detectTypes,
			['current_version_mask']: params.readMask,
			['parsing_mode']: params.parsingMode,
			['additional_params']: params.additionalParams,
			id: params.id,
			path: params.path,
			type: params.depotType.toUpperCase(),
			charset: params.charset,
			schema:
				params.config.schema === null
					? {
							children: [],
							keep: false,
					  }
					: params.config.schema,
			metadata: {
				label: params.label,
				actif: params.isActiveDepot,
				code: params.code,
			},
		};

		if (params.depotType === 'json') {
			body = {
				...body,
				type: 'JSON_V2',
				['json_line']: params.isMultiLine,
			};
		} else if (params.depotType === 'xml') {
			body = {
				...body,
				type: 'XML_V2',
				['row_tag']: params.newLineTag,
				['ignore_surrounding_spaces']: params.shouldIgnoreSurroundingSpace,
				['treat_empty_values_as_nulls']: params.hasNullForEmptyValues,
			};
		} else if (params.depotType === 'trino') {
			body = {
				...body,
				type: 'TRINO',
				['query']: params.query,
			};
		} else if (params.depotType === 'datalink') {
			body = {
				...body,
				type: 'DATALINK',
				// L'id nomme la version lue, l'uuid reste le seul nom quand elle n'existe plus.
				[camelToSnake('dataLinkId')]: params.dataLinkId,
				[camelToSnake('dataLinkUuid')]: params.dataLinkUuid,
			};
		}
		return this.http.post<IDepotSchemaModel>(url, body);
	}

	public createRepository(
		metadata: AppMetadataEntity,
		depotType: string,
		subscription: SubscriptionForDepotItemEntity,
		allowProducerVersionSwitch: boolean
	): Observable<IRepositoryModel> {
		const resourceUrl = this.generateResourceUrl('REPOSITORIES_PRIVATE');
		let body: IRepositoryModel = {};

		if (depotType === 'DATALINK') {
			body = this.generateSubscriptionRepositoryPayload(
				metadata,
				depotType,
				subscription,
				allowProducerVersionSwitch
			);
		}

		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
				ctx: 0,
			}
		);

		return this.http.post<IRepositoryModel>(url, body);
	}

	public getAvailableVersionsForDataLinkRepo(
		depotId: number
	): Observable<ReadonlyArray<IDataLinkRepoVersion>> {
		const resourceUrl = this.generateResourceUrl(
			'REPOSITORY_DATALINK_VERSIONS'
		);

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				repositoryId: depotId,
			},
			{
				project: this.projectId,
			}
		);

		return this.http.get<ReadonlyArray<IDataLinkRepoVersion>>(url);
	}

	public changeVersionForDataLinkRepo(
		depotId: number,
		newVersion: number
	): Observable<any> {
		const resourceUrl = this.generateResourceUrl(
			'REPOSITORY_DATALINK_VERSION_SWITCH'
		);

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				repositoryId: depotId,
			},
			{
				targetVersion: newVersion,
				project: this.projectId,
			}
		);

		return this.http.post<any>(url, {});
	}

	private generateSubscriptionRepositoryPayload(
		metadata: AppMetadataEntity,
		depotType: string,
		subscription: SubscriptionForDepotItemEntity,
		allowProducerVersionSwitch: boolean
	): ISubscriptionRepositoryModel {
		return {
			metadata: {
				label: metadata.label,
				description: metadata.description,
				tags: this.generateMetadataTagPayload(metadata.tags.toArray()),
				actif: metadata.isActive,
			},
			type: depotType,
			[camelToSnake('dataLinkId')]: Number(subscription.id),
			[camelToSnake('allowProducerVersionSwitch')]: allowProducerVersionSwitch,
		};
	}

	private generateDepotEntityPayload(
		metadata: AppMetadataEntity,
		sourceDepotHeaders: Array<DepotHeaderItemEntity>,
		mappings: Array<DepotHeaderItemEntity>,
		availableCharacteristicTypes: Array<CharacteristicType>
	): IDepotEntityCreate {
		const payload: IDepotEntityCreate = {
			caracs: [],
			entity: {
				actif: metadata.isActive,
				description: metadata.description,
				label: metadata.label,
				[camelToSnake('iconId')]: metadata.iconId,
				tags: this.generateMetadataTagPayload(metadata.tags.toArray()),
			},
			mappings: [],
		};
		sourceDepotHeaders.map((element, index) => {
			const mappingElement = mappings.find((m) => m.id === element.id);
			if (!mappingElement) {
				return;
			}

			const characteristicType = availableCharacteristicTypes.find(
				(t) => t.value === element.columnType
			);

			if (!characteristicType) {
				return;
			}

			const normalizedCode =
				'_' + elementCodeNormalizer(mappingElement.label) + '_' + index;

			payload.caracs.push({
				[camelToSnake('originColumnId')]: normalizedCode,
				oldCode: mappingElement.label,
				label: element.label,
				lib: element.label,
				poids: 100,
				complex: false,
				depotDetails: mappingElement.depotDetails,
				[camelToSnake('datePattern')]: element.format,
				[camelToSnake('entiteType')]:
					this.generateCharacteristicType(characteristicType),
				list: element.isList,
			});

			const headerMapping = {
				header: {
					id: mappingElement.id,
				},
				[camelToSnake('isFormula')]: false,
				[camelToSnake('ucaCode')]: normalizedCode,
			};

			let updateMapping = headerMapping;

			if (element.format) {
				updateMapping = {
					...updateMapping,
					['pattern']: element.format,
				};
			}

			if (element.language) {
				updateMapping = {
					...updateMapping,
					['language']: element.language,
				};
			}

			if (element.timezone) {
				updateMapping = {
					...updateMapping,
					['timezone']: element.timezone,
				};
			}

			payload.mappings.push(updateMapping);
		});
		return payload;
	}

	private generateCharacteristicType(
		characteristicType: CharacteristicType
	): ICharacteristicType {
		return {
			id: characteristicType.id,
			code: ColumnTypeMap[characteristicType.value],
			lib: characteristicType.label,
			selected: false,
		};
	}

	private generateMetadataTagPayload(
		tags: Array<AppTagItemEntity>
	): Array<IMetadataTag> {
		return tags.map((t) => ({
			id: t.id !== -1 ? t.id : undefined,
			code: t.label,
			color: t.color,
			position: t.position,
		}));
	}

	// TODO: replaces with mapping to visually see handled cases of XML_V2 and JSON_V2
	private getApiKeyByType(type: string): string {
		if (type.includes('CSV')) {
			return 'CSV_DEPOT_BY_ID';
		}
		if (
			type.includes('TRINO') ||
			type.includes('_V2') ||
			type.includes('DATALINK')
		) {
			return 'REPOSITORIES_PRIVATE_BY_ID';
		}
		if (type.includes('EXCEL')) {
			return 'EXCEL_DEPOT_BY_ID';
		}
		if (type.includes('XML')) {
			return 'XML_DEPOT_BY_ID';
		}
		if (type.includes('JSON')) {
			return 'JSON_DEPOT_BY_ID';
		}
		if (type.includes('PARQUET')) {
			return 'PARQUET_DEPOT_BY_ID';
		}
		if (type.includes('BINARY')) {
			return 'BINARY_DEPOT_BY_ID';
		}
		if (type.includes('SQL')) {
			return 'DB_SQL_DEPOT_BY_ID';
		}
		if (type.includes('NEO4J')) {
			return 'NEO4J_DEPOT_BY_ID';
		}
		if (type.includes('MONGODB')) {
			return 'MONGODB_DEPOT_BY_ID';
		}
		throw new Error(`Unrecognized type: ${type}`);
	}

	// TODO: replaces with mapping to visually see handled cases of XML_V2 and JSON_V2
	private getActiveStatusApiKeyByType(type: string): string {
		if (type.includes('CSV')) {
			return 'CSV_DEPOTS_ACTIVE';
		}
		if (type.includes('EXCEL')) {
			return 'EXCEL_DEPOTS_ACTIVE';
		}
		if (type.includes('XML')) {
			return 'XML_DEPOTS_ACTIVE';
		}
		if (type.includes('JSON')) {
			return 'JSON_DEPOTS_ACTIVE';
		}

		if (type.includes('PARQUET')) {
			return 'PARQUET_DEPOTS_ACTIVE';
		}
		if (type.includes('BINARY')) {
			return 'BINARY_DEPOTS_ACTIVE';
		}
		if (type.includes('SQL')) {
			return 'DB_SQL_DEPOTS_ACTIVE';
		}
		if (type.includes('NEO4J')) {
			return 'NEO4J_DEPOTS_ACTIVE';
		}
		if (type.includes('MONGODB')) {
			return 'MONGODB_DEPOTS_ACTIVE';
		}

		if (type.includes('DATALINK')) {
			return 'REPOSITORIES_ACTIVE';
		}

		throw new Error(`Unrecognized type: ${type}`);
	}

	// TODO: replaces with mapping to visually see handled cases of XML_V2 and JSON_V2
	private getHeadersApiKeyByType(type: string): string {
		if (type.includes('CSV')) {
			return 'CSV_DEPOT_HEADERS';
		}
		if (type.includes('EXCEL')) {
			return 'EXCEL_DEPOT_HEADERS';
		}
		if (type.includes('XML')) {
			return 'XML_DEPOT_HEADERS';
		}
		if (type.includes('JSON')) {
			return 'JSON_DEPOT_HEADERS';
		}
		if (type.includes('PARQUET')) {
			return 'PARQUET_DEPOT_HEADERS';
		}
		if (type.includes('BINARY')) {
			return 'BINARY_DEPOT_HEADERS';
		}
		if (type.includes('SQL')) {
			return 'DB_SQL_DEPOT_HEADERS';
		}
		if (type.includes('NEO4J')) {
			return 'NEO4J_DEPOT_HEADERS';
		}
		if (type.includes('MONGODB')) {
			return 'MONGODB_DEPOT_HEADERS';
		}
		if (type.includes('TRINO') || type.includes('DATALINK')) {
			return 'REPOSITORIES_HEADERS';
		}
		throw new Error(`Unrecognized type: ${type}`);
	}
}
